import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ChakraSelect,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  useToast,
  VStack,
} from '@elkaso-app/web-design';
import type { MasterProductUsage, MergeCollisionResolution, MergePlanCollision } from 'apis/master-products';
import {
  masterProductsEndpoint,
  useGetMasterProductMergePlanApi,
  useGetMasterProductUsageApi,
  useMergeMasterProductApi,
} from 'apis/master-products';
import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';

interface MergeVariantModalProps {
  isOpen: boolean;
  onClose: () => void;
  variantId: number | undefined;
  variantLabel: string;
  // Every OTHER saved variant in this family. Sourced from the grid, which already has
  // them — no search endpoint needed, and merging is same-family by definition.
  siblings: { id: number; label: string }[];
  onMerged: (result: { survivorId: number; loserId: number }) => void;
}

// Informational only — usage never gates the merge. Count is a poor proxy for data
// quality (the heavily-used row is often the sloppy one a sheet upload created first),
// which is exactly why the survivor is picked by hand.
const UsageSummary: React.FC<{ usage?: MasterProductUsage; isLoading: boolean }> = ({ usage, isLoading }) => {
  if (isLoading) return <Spinner size='xs' />;
  if (!usage) return null;
  const parts = [
    usage.orders.length > 0 ? `${usage.orders.length}${usage.hasMoreOrders ? '+' : ''} orders` : null,
    usage.inquiryItems > 0 ? `${usage.inquiryItems} inquiry items` : null,
    usage.supplierProducts > 0 ? `${usage.supplierProducts} supplier products` : null,
    usage.deals > 0 ? `${usage.deals} prices` : null,
  ].filter(Boolean);
  return (
    <Text fontSize='xs' color='gray.600'>
      {parts.length ? parts.join(' · ') : 'Not used anywhere'}
    </Text>
  );
};

const CollisionCard: React.FC<{
  collision: MergePlanCollision;
  resolution: MergeCollisionResolution;
  onChange: (resolution: MergeCollisionResolution) => void;
}> = ({ collision, resolution, onChange }) => {
  const clones = collision.privateClones;
  return (
    <Box borderWidth='1px' borderColor='gray.200' borderRadius='md' p='sm'>
      <Text fontSize='sm' fontWeight='semibold'>
        {collision.supplierName ?? `Supplier #${collision.supplierId}`}
      </Text>
      <Text fontSize='sm' color='gray.700'>
        {collision.survivorProductName
          ? `The kept variant already has “${collision.survivorProductName}” from this supplier.`
          : 'The kept variant already has a price from this supplier.'}
      </Text>
      <Text fontSize='sm' color='gray.700'>
        {collision.loserProductName
          ? `“${collision.loserProductName}”`
          : 'This supplier’s price for the deleted variant'}
        {collision.loserDealsCount > 0 &&
          ` carries ${collision.loserDealsCount} price${collision.loserDealsCount === 1 ? '' : 's'}`}
        {clones.length > 0 &&
          ` and ${clones.length} restaurant private catalog item${clones.length === 1 ? '' : 's'} (${clones
            .map((clone) => clone.ownerName ?? `#${clone.productId}`)
            .join(', ')})`}
        .
      </Text>
      <RadioGroup mt='xs' value={resolution} onChange={(value) => onChange(value as MergeCollisionResolution)}>
        <HStack spacing='md'>
          <Radio value='unlink' size='sm'>
            Unlink
          </Radio>
          <Radio value='delete' size='sm'>
            Delete
          </Radio>
        </HStack>
      </RadioGroup>
      <Text fontSize='xs' color='gray.600' mt='xs'>
        {resolution === 'unlink'
          ? 'Keeps the supplier’s product in the catalog, unmapped — it drops out of quote suggestions until someone re-maps it.'
          : 'Moves the supplier’s open quotes and catalog items above to the kept variant’s product from the same supplier, then removes the old entry and its prices. Past orders are unaffected.'}
        {resolution === 'delete' &&
          collision.droppedAwardsOnDelete > 0 &&
          ` Drops ${collision.droppedAwardsOnDelete} award${
            collision.droppedAwardsOnDelete === 1 ? '' : 's'
          } that will need re-awarding.`}
      </Text>
    </Box>
  );
};

export const MergeVariantModal: React.FC<MergeVariantModalProps> = ({
  isOpen,
  onClose,
  variantId,
  variantLabel,
  siblings,
  onMerged,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  // `swapped` rather than a loserId/survivorId pair: the select always means "the other
  // variant", so flipping roles must not change what it points at.
  const [counterpartId, setCounterpartId] = useState<number | undefined>(undefined);
  const [swapped, setSwapped] = useState(false);
  const [resolutions, setResolutions] = useState<Record<number, MergeCollisionResolution>>({});

  useEffect(() => {
    if (!isOpen) {
      setCounterpartId(undefined);
      setSwapped(false);
      setResolutions({});
    }
  }, [isOpen]);

  // A family of exactly two variants leaves nothing to pick — auto-select the lone
  // sibling so the merge is one click instead of a forced dropdown round-trip.
  // Keyed on the sibling's *id*, never on the `siblings` array reference: callers build
  // that array inline during render, so a reference dep would refire on every unrelated
  // re-render and silently re-assert a counterpart the user had explicitly cleared.
  const soleSiblingId = siblings.length === 1 ? siblings[0].id : undefined;
  useEffect(() => {
    if (isOpen && soleSiblingId !== undefined) {
      setCounterpartId(soleSiblingId);
    }
  }, [isOpen, soleSiblingId]);

  const loserId = swapped ? counterpartId : variantId;
  const survivorId = swapped ? variantId : counterpartId;

  // A collision resolution is a decision about a specific loser→survivor direction.
  // Swapping roles or picking a different counterpart changes that direction (or the
  // pairing entirely), so any resolution picked for the old direction must not silently
  // carry over — otherwise a Delete chosen for "unlink A's Sunrise entry" could fire
  // against B's Sunrise entry after a Swap, deleting the side the user meant to keep.
  useEffect(() => {
    setResolutions({});
  }, [loserId, survivorId]);

  const {
    data: plan,
    isLoading: isPlanLoading,
    isError: isPlanError,
  } = useGetMasterProductMergePlanApi(loserId, survivorId, { enabled: isOpen });

  const { data: loserUsage, isLoading: isLoserUsageLoading } = useGetMasterProductUsageApi(loserId, {
    enabled: isOpen && !!loserId,
  });
  const { data: survivorUsage, isLoading: isSurvivorUsageLoading } = useGetMasterProductUsageApi(survivorId, {
    enabled: isOpen && !!survivorId,
  });

  const { mutate: merge, isLoading: isMerging } = useMergeMasterProductApi({
    onSuccess: (result) => {
      toast({ title: `Merged into “${plan?.survivor.label ?? ''}”`, status: 'success' });
      onMerged({ survivorId: result.survivorId, loserId: result.loserId });
      onClose();
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      if (status === 404) {
        // Spec: one of the variants was already gone by the time this posted — toast,
        // invalidate, and close, mirroring delete-variant-modal.tsx's 404 handling.
        // Nothing else in this modal gates on the error, so leaving it open would let
        // the user re-fire the same 404 against a ghost pairing forever.
        const message =
          error?.response?.data?.message || 'One of these variants no longer exists. Refresh and try again.';
        toast({ title: message, status: 'error' });
        queryClient.invalidateQueries([masterProductsEndpoint]);
        onClose();
        return;
      }
      const message = error?.response?.data?.message || 'Failed to merge. Please try again.';
      toast({ title: message, status: 'error' });
    },
  });

  const options = useMemo(() => siblings.map((sibling) => ({ label: sibling.label, value: sibling.id })), [siblings]);
  const selectedOption = options.find((option) => option.value === counterpartId) ?? null;
  const selectStyles = { menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) };
  const portalTarget = typeof document !== 'undefined' ? document.body : undefined;

  const collisions = plan?.supplierCollisions ?? [];
  // Only sent by the v1 backend — kept for the deploy window (see the alert below).
  const blockingInquiries = plan?.blockingInquiries ?? [];
  const foldInquiries = plan?.foldInquiries ?? [];
  const canMerge = !!plan?.canMerge && !isPlanLoading && !isPlanError && !!loserId && !!survivorId;

  const handleConfirm = () => {
    if (!loserId || !survivorId) return;
    merge({
      loserId,
      survivorId,
      // Unlink is the default, so an untouched collision still posts a decision.
      collisions: collisions.map((collision) => ({
        supplierId: collision.supplierId,
        resolution: resolutions[collision.supplierId] ?? 'unlink',
      })),
    });
  };

  const loserLabel = plan?.loser.label ?? (swapped ? selectedOption?.label ?? '' : variantLabel);
  const survivorLabel = plan?.survivor.label ?? (swapped ? variantLabel : selectedOption?.label ?? '');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: 'full', md: 'xl' }}
      scrollBehavior='inside'
      closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Merge Variant</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align='stretch'>
            <FormControl>
              <FormLabel fontSize='sm'>Merge with</FormLabel>
              <ChakraSelect
                options={options}
                value={selectedOption}
                onChange={(option: any) => setCounterpartId(option?.value)}
                isClearable
                placeholder='Pick another variant in this family…'
                menuPortalTarget={portalTarget}
                styles={selectStyles}
              />
            </FormControl>

            <Stack direction={{ base: 'column', md: 'row' }} align='stretch' spacing='sm'>
              <Box flex='1' borderWidth='1px' borderColor='red.200' borderRadius='md' p='sm'>
                <Text fontSize='xs' color='red.600' fontWeight='semibold'>
                  WILL BE DELETED
                </Text>
                <Text fontSize='sm'>{loserLabel || '—'}</Text>
                <UsageSummary usage={loserUsage} isLoading={isLoserUsageLoading} />
              </Box>
              <VStack justify='center'>
                <Button size='xs' variant='outline' onClick={() => setSwapped((v) => !v)} isDisabled={!counterpartId}>
                  Swap
                </Button>
              </VStack>
              <Box flex='1' borderWidth='1px' borderColor='green.200' borderRadius='md' p='sm'>
                <Text fontSize='xs' color='green.700' fontWeight='semibold'>
                  WILL BE KEPT
                </Text>
                <Text fontSize='sm'>{survivorLabel || '—'}</Text>
                <UsageSummary usage={survivorUsage} isLoading={isSurvivorUsageLoading} />
              </Box>
            </Stack>

            {isPlanLoading && !!loserId && !!survivorId && (
              <HStack spacing={2}>
                <Spinner size='sm' />
                <Text color='gray.600'>Working out what this merge would do…</Text>
              </HStack>
            )}

            {isPlanError && (
              <Alert status='error'>
                <AlertIcon />
                Couldn&rsquo;t work out what merging these variants would do. Please try again.
              </Alert>
            )}

            {blockingInquiries.length > 0 && (
              <Alert status='error' alignItems='flex-start'>
                <AlertIcon />
                <VStack align='stretch' spacing={1}>
                  <Text>
                    These inquiries contain both variants. Merging would leave each one holding two identical items, so
                    remove the duplicate item from each, then merge:
                  </Text>
                  <HStack spacing={2} wrap='wrap'>
                    {blockingInquiries.map(({ inquiryId, restaurantName }) => (
                      <Link
                        key={inquiryId}
                        as={RouterLink}
                        to={`/app/inquiry-processor/list?inquiryId=${inquiryId}`}
                        target='_blank'
                        color='blue.600'
                        fontSize='sm'>
                        #{inquiryId}
                        {restaurantName ? ` — ${restaurantName}` : ''}
                      </Link>
                    ))}
                  </HStack>
                </VStack>
              </Alert>
            )}

            {foldInquiries.length > 0 && (
              <Alert status='info' alignItems='flex-start'>
                <AlertIcon />
                <VStack align='stretch' spacing={1}>
                  <Text>
                    These inquiries contain both variants — each one&rsquo;s lines will be folded into one. The kept
                    line&rsquo;s volume is unchanged.
                  </Text>
                  <VStack align='stretch' spacing={0}>
                    {foldInquiries.map((fold) => (
                      <HStack key={fold.inquiryId} spacing={2} wrap='wrap'>
                        <Link
                          as={RouterLink}
                          to={`/app/inquiry-processor/list?inquiryId=${fold.inquiryId}`}
                          target='_blank'
                          color='blue.600'
                          fontSize='sm'>
                          #{fold.inquiryId}
                          {fold.restaurantName ? ` — ${fold.restaurantName}` : ''}
                        </Link>
                        <Text fontSize='sm' color='gray.600'>
                          {fold.quotesMoved} quotes moved · {fold.quotesRejected} rejected
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                  {(plan?.droppedAwards ?? 0) > 0 && (
                    <Text fontSize='sm' fontWeight='semibold'>
                      {plan?.droppedAwards} awards will need re-awarding.
                    </Text>
                  )}
                </VStack>
              </Alert>
            )}

            {!!plan && blockingInquiries.length === 0 && (
              <Text fontSize='sm' color='gray.700'>
                Will repoint {plan.repoint.inquiryItems} inquiry items, {plan.repoint.supplierProducts} supplier
                products, {plan.repoint.deals} prices
                {plan.repoint.privateProducts > 0 && `, ${plan.repoint.privateProducts} private catalog items`}.
              </Text>
            )}

            {collisions.length > 0 && (
              <>
                <Divider />
                <Text fontSize='sm' fontWeight='semibold'>
                  These suppliers sell both variants — choose what happens to the deleted variant&rsquo;s entry:
                </Text>
                <VStack align='stretch' spacing='sm'>
                  {collisions.map((collision) => (
                    <CollisionCard
                      key={collision.supplierId}
                      collision={collision}
                      resolution={resolutions[collision.supplierId] ?? 'unlink'}
                      onChange={(resolution) =>
                        setResolutions((prev) => ({ ...prev, [collision.supplierId]: resolution }))
                      }
                    />
                  ))}
                </VStack>
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant='outline' mr={3} onClick={onClose} isDisabled={isMerging}>
            Cancel
          </Button>
          <Button
            colorScheme='red'
            onClick={handleConfirm}
            isDisabled={!canMerge}
            isLoading={isMerging}
            loadingText='Merging...'>
            Merge &amp; delete {loserLabel ? `“${loserLabel}”` : 'variant'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
